import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Panel, StatusPill, Loading } from "@/components/portal/widgets";
import TripMap from "@/components/portal/TripMap";
import TripStatusDialog from "@/components/portal/TripStatusDialog";
import TripTimeDialog, { ChangeRow } from "@/components/portal/TripTimeDialog";
import ReassignDialog from "@/components/portal/ReassignDialog";
import AddressEditDialog from "@/components/portal/AddressEditDialog";
import { ArrowLeft, Phone, Pencil, FileDown, Loader2 } from "lucide-react";
import { fmtDateTime, fmtZoned } from "@/lib/time";
import api from "@/lib/api";
import { useLiveRefresh, UpdatedAgo, ago } from "@/lib/liveRefresh";
import { isStale } from "@/components/portal/LiveMap";
import { RiskBadge } from "@/components/portal/widgets";
import { WillCallBadge, RiderReadyButton, SetStatusDialog } from "@/components/portal/TripStatusTools";
import { isInProgress } from "@/lib/tripStatus";
import { fmtTime } from "@/lib/time";

const AddrStatus = ({ field, info, lat }) => {
  const edit = (info.edits || []).find((e) => e.field === field);
  const flag = (info.open_flags || []).find((x) => x.field === `${field}_address`);
  const [label, cls] = flag ? ["Needs correction", "border-red-500/40 bg-red-500/10 text-red-300"]
    : edit?.verification === "manual_unverified" ? ["Entered by hand · unverified", "border-amber-500/40 bg-amber-500/10 text-amber-300"]
    : edit?.verification === "verified" ? ["Verified", "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"] : ["As imported", "border-slate-600 bg-slate-800/60 text-slate-400"];
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`trip-${field}-addr-status`}>
      <span className={`rounded-full border px-2 py-0.5 font-semibold ${cls}`}>{label}</span>
      <span className={`rounded-full border px-2 py-0.5 ${typeof lat === "number" ? "border-slate-600 text-slate-400" : "border-amber-500/40 text-amber-300"}`}>
        {typeof lat === "number" ? "On map" : "Not on map — routing/ETA unavailable"}
      </span>
      {edit && <span className="text-slate-500">by {edit.by} · {fmtDateTime(edit.at)}{edit.reason ? ` — “${edit.reason}”` : ""}</span>}
    </div>
  );
};

const Row = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2 text-sm" data-testid={testid}>
    <span className="shrink-0 text-slate-500">{label}</span>
    <span className="text-right font-medium text-slate-100">{children ?? "—"}</span>
  </div>
);

const ActionBtn = ({ onClick, children, testid, tone = "slate" }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors ${tone === "blue" ? "bg-blue-700 text-white hover:bg-blue-800" : "border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"}`}>
    {children}
  </button>
);

export default function TripDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const canEdit = ["owner", "supervisor", "dispatcher"].includes(user?.role);
  const canEditAddr = ["owner", "dispatcher", "administrator"].includes(user?.role);
  const canRiderReady = ["owner", "supervisor", "dispatcher", "fleet_manager"].includes(user?.role);
  const [addrInfo, setAddrInfo] = useState({ edits: [], open_flags: [] });
  const [t, setT] = useState(null);
  const [extra, setExtra] = useState({ path: [], contacts: [], linked: null, times: null });
  const [driverPos, setDriverPos] = useState(null);
  const [dialog, setDialog] = useState("");
  const [pdf, setPdf] = useState(false);
  const [addrField, setAddrField] = useState("pickup");
  useEffect(() => { if (dialog.startsWith("addr-")) setAddrField(dialog.slice(5)); }, [dialog]);

  const load = useCallback(async () => {
    try {
      const { data } = await fleetApi.get(`/trips/${id}`);
      setT(data);
      const [p, c, l, tc, ai] = await Promise.all([
        fleetApi.get(`/trips/${id}/path`).catch(() => ({ data: {} })),
        fleetApi.get(`/trips/${id}/contacts`).catch(() => ({ data: [] })),
        fleetApi.get(`/trips/${id}/linked`).catch(() => ({ data: null })),
        fleetApi.get(`/trips/${id}/time-changes`).catch(() => ({ data: null })),
        api.get(`/addresses/edits/${id}`).catch(() => ({ data: { edits: [], open_flags: [] } })),
      ]);
      setExtra({ path: p.data?.points || [], contacts: Array.isArray(c.data) ? c.data : [], linked: l.data?.id ? l.data : null, times: tc.data });
      setAddrInfo(ai.data);
    } catch (e) {
      setT((prev) => { if (!prev) toast.error(fleetError(e, "Couldn't load this trip.")); return prev || false; });
      throw e;
    }
  }, [id]);

  useEffect(() => { load().catch(() => {}); }, [load]);
  const updatedAt = useLiveRefresh(load, 15000);

  useEffect(() => {
    if (!t?.assigned_driver_id) return undefined;
    const poll = () => fleetApi.get("/live-map").then((r) => {
      const d = (r.data || []).find((x) => x.driver_id === t.assigned_driver_id);
      setDriverPos(d && typeof d.lat === "number" && d.last_ping ? { lat: d.lat, lng: d.lng, last_ping: d.last_ping, label: `${d.full_name || d.name}${isStale(d.last_ping) ? ` · last seen ${ago(d.last_ping)}` : ""}`, eta: d.current_trip?.id === t.id ? d.current_trip : null } : null);
    }).catch(() => {});
    poll();
    const iv = setInterval(poll, 15000);
    return () => clearInterval(iv);
  }, [t?.assigned_driver_id, t?.id]);

  const downloadPdf = async () => {
    setPdf(true);
    try {
      const r = await fleetApi.get(`/trips/${id}/evidence-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `trip-${t.external_trip_id || id}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(fleetError(e, "Couldn't download the evidence PDF.")); }
    finally { setPdf(false); }
  };

  if (t === null) return <Loading />;
  if (t === false) return <div className="py-16 text-center text-sm text-slate-400" data-testid="trip-not-found">Trip not found.</div>;

  const assigned = t.assigned_driver_id || t.assigned_driver_name;
  const eta = driverPos?.eta;
  const close = (v) => { if (!v) setDialog(""); };
  const refresh = () => { setDialog(""); load(); };

  return (
    <div data-testid="trip-detail-page">
      <button onClick={() => navigate(-1)} data-testid="trip-back" className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="font-mono-data text-xs text-slate-500" data-testid="trip-external-id">{t.external_trip_id} · {t.broker || "—"} · {t.trip_type || "—"}</p>
          <h1 className="mt-1 text-2xl font-bold text-white sm:text-3xl" data-testid="trip-rider-name">{t.rider_name}</h1>
          <div className="mt-2 flex flex-wrap gap-2"><StatusPill value={t.status} /><WillCallBadge trip={t} testid="trip-willcall-badge" /><RiskBadge trip={t} testid="trip-risk-badge" /><UpdatedAgo at={updatedAt} testid="trip-live-updated" /></div>
          {t.will_call_ready_at && <p className="mt-1.5 text-xs text-emerald-300" data-testid="trip-rider-ready-since">Rider ready since {fmtTime(t.will_call_ready_at)}{(t.will_call_ready_by_name || t.will_call_ready_by) ? ` · ${t.will_call_ready_by_name || t.will_call_ready_by}` : ""}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {canRiderReady && t.status === "Will Call" && !t.will_call_ready_at && <RiderReadyButton trip={t} onDone={() => load()} />}
          {(isOwner || (user?.role === "dispatcher" && !isInProgress(t.status))) && (
            <ActionBtn onClick={() => setDialog("setstatus")} testid="trip-action-change-status">Change Status</ActionBtn>
          )}
        {canEdit && (
          <>
            <ActionBtn onClick={() => setDialog("status")} testid="trip-action-status">Update status</ActionBtn>
            <ActionBtn onClick={() => navigate(`/portal/time-fixes?trip=${t.id}`)} testid="trip-action-time">Change time</ActionBtn>
            {isOwner && t.status !== "On Hold" && <ActionBtn tone="blue" onClick={() => setDialog("assign")} testid="trip-action-assign">{assigned ? "Reassign driver" : "Assign driver"}</ActionBtn>}
            <ActionBtn onClick={downloadPdf} testid="trip-action-pdf">{pdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Evidence PDF</ActionBtn>
          </>
        )}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
        <Panel title="Live trip map" testid="trip-map-panel"
          action={<span className="font-mono-data text-xs text-slate-500" data-testid="trip-map-live">{driverPos ? (isStale(driverPos.last_ping) ? `Stale — last position ${ago(driverPos.last_ping)}` : `Driver live · ${ago(driverPos.last_ping)}${eta?.eta_min != null ? ` · ETA ${eta.eta_min} min to ${eta.eta_to}` : ""}`) : assigned ? "No live position from driver — tracking off or not on duty" : "No driver yet"}</span>}>
          <div className="p-4">
            <TripMap pickup={{ lat: t.pickup_lat, lng: t.pickup_lng, label: t.pickup_address }}
              dropoff={{ lat: t.dropoff_lat, lng: t.dropoff_lng, label: t.dropoff_name || t.dropoff_address }}
              path={extra.path} driver={driverPos} />
            <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-400">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-green-500" /> Pickup</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Drop-off</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-400" /> Driver</span>
              <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-blue-500" /> Route driven</span>
            </div>
          </div>
        </Panel>

        <div className="space-y-6">
          <Panel title="Schedule" testid="trip-schedule">
            <div className="divide-y divide-slate-800 px-5 py-2">
              <Row label="Pickup" testid="trip-pickup-time">{fmtZoned(t.scheduled_pickup)}</Row>
              <Row label="Appointment">{t.appointment_time ? fmtZoned(t.appointment_time) : "—"}</Row>
              <Row label="Return">{t.scheduled_return ? fmtDateTime(t.scheduled_return) : "—"}</Row>
              <Row label="Predicted delay">{t.predicted_delay_min ? `${t.predicted_delay_min} min` : "On time"}</Row>
              <Row label="Navigation">{t.navigation_started_at ? `Started ${fmtDateTime(t.navigation_started_at)}${t.eta_min != null ? ` · ETA ${t.eta_min} min${t.eta_to ? ` to ${t.eta_to}` : ""}` : ""}` : "Not started"}</Row>
              <Row label="Picked up">{t.pickup_timestamp ? fmtDateTime(t.pickup_timestamp) : "—"}</Row>
              <Row label="Dropped off">{t.dropoff_timestamp ? fmtDateTime(t.dropoff_timestamp) : "—"}</Row>
              <Row label="Price">{t.price != null ? `$${Number(t.price).toFixed(2)}` : "—"}</Row>
            </div>
          </Panel>
          <Panel title="Driver & vehicle" testid="trip-driver">
            <div className="divide-y divide-slate-800 px-5 py-2">
              <Row label="Driver">{t.assigned_driver_id ? <Link to={`/portal/drivers/${t.assigned_driver_id}`} className="text-blue-300 hover:underline" data-testid="trip-driver-link">{t.assigned_driver_name}</Link> : t.assigned_driver_name || "Unassigned"}</Row>
              <Row label="Vehicle">{t.assigned_vehicle_fleet || "—"}</Row>
            </div>
          </Panel>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Route" testid="trip-route">
          <div className="space-y-4 px-5 py-4 text-sm">
            {[["pickup", "Pickup", t.pickup_address, null], ["dropoff", "Drop-off", t.dropoff_address, t.dropoff_name]].map(([f, label, addr, name]) => (
              <div key={f} className="flex items-start justify-between gap-3">
                <div><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
                  {name && <p className="font-semibold text-white">{name}</p>}
                  <p className="text-slate-200" data-testid={`trip-${f}-address`}>{addr}</p>
                  <AddrStatus field={f} info={addrInfo} lat={f === "pickup" ? t.pickup_lat : t.dropoff_lat} /></div>
                {canEditAddr && <button onClick={() => setDialog(`addr-${f}`)} data-testid={`trip-edit-${f}`} className="shrink-0 rounded p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white" title={`Edit ${label.toLowerCase()} address`}><Pencil className="h-3.5 w-3.5" /></button>}
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Rider" testid="trip-rider">
          <div className="divide-y divide-slate-800 px-5 py-2">
            <Row label="Phone">{t.rider_phone ? <a href={`tel:${t.rider_phone}`} className="text-blue-300 hover:underline">{t.rider_phone}</a> : "—"}</Row>
            <Row label="Wheelchair">{t.wheelchair ? "Yes" : "No"}</Row>
            <Row label="Escort">{t.escort ? "Yes" : "No"}</Row>
            <Row label="Mobility notes">{t.mobility_notes || "—"}</Row>
            <Row label="Instructions">{t.special_instructions || "—"}</Row>
          </div>
        </Panel>
        <Panel title="Contacts" testid="trip-contacts">
          <div className="divide-y divide-slate-800">
            {extra.contacts.length === 0 && <p className="px-5 py-6 text-center text-sm text-slate-500">No contacts.</p>}
            {extra.contacts.map((c, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-2.5 text-sm">
                <div><p className="font-semibold text-white">{c.name}</p><p className="text-xs text-slate-500">{c.role}</p></div>
                {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-xs text-blue-300 hover:underline"><Phone className="h-3 w-3" />{c.phone}</a>}
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="History" testid="trip-history">
          <div className="space-y-3 px-5 py-4 text-sm">
            {extra.linked && (
              <Link to={`/portal/trips/${extra.linked.id}`} data-testid="trip-linked-link" className="block rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2 hover:bg-slate-800">
                <p className="text-xs uppercase tracking-wider text-slate-500">Linked leg</p>
                <p className="text-white">{extra.linked.external_trip_id} · {fmtDateTime(extra.linked.scheduled_pickup)} · {extra.linked.status}</p>
              </Link>
            )}
            {(extra.times?.changes || []).length === 0 ? <p className="text-slate-500">No manual time changes.</p> : (
              <ul className="space-y-2">{extra.times.changes.map((c, i) => <ChangeRow key={c.id || i} c={c} i={i} />)}</ul>
            )}
          </div>
        </Panel>
      </div>

      <SetStatusDialog trip={t} open={dialog === "setstatus"} onOpenChange={close} onDone={refresh} />
      <TripStatusDialog trip={t} open={dialog === "status"} onOpenChange={close} onUpdated={refresh} />
      <TripTimeDialog trip={t} open={dialog === "time"} onOpenChange={close} onUpdated={refresh} />
      {isOwner && <ReassignDialog trip={dialog === "assign" ? t : null} open={dialog === "assign"} onOpenChange={close} onReassigned={refresh} />}
      <AddressEditDialog trip={t} field={addrField} open={dialog.startsWith("addr-")} onOpenChange={close} onSaved={load} />
    </div>
  );
}
